import { useCallback, useEffect, useMemo, useState } from 'react';
import { GroupingConfig, TreeNode } from '../types';
import { groupDataByKey } from '../utils';

export function useGrouping<TData>(
  data: TreeNode<TData>[],
  groupingConfig?: GroupingConfig<TreeNode<TData>>
) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  const getGroupKeyFn = useCallback(() => {
    if (!groupingConfig?.isEnabled) return null;
    if (groupingConfig.toolbar?.isEnabled && groupingConfig.toolbar?.value) {
      const selectedOption = groupingConfig.toolbar.options.find(
        (opt) => opt.id === groupingConfig.toolbar?.value
      );
      if (selectedOption)
        return selectedOption.getGroupKey as (row: TreeNode<TData>) => string;
    }
    return groupingConfig?.getGroupKey as
      | ((row: TreeNode<TData>) => string)
      | null;
  }, [groupingConfig]);

  const groupKeyFn = getGroupKeyFn();

  const groupedData = useMemo(() => {
    return groupKeyFn
      ? groupDataByKey(
          data as unknown as TData[],
          groupKeyFn as (row: unknown) => string
        )
      : new Map<string, TData[]>();
  }, [data, groupKeyFn]);

  useEffect(() => {
    if (!groupingConfig?.isEnabled) return;

    if (!groupKeyFn) return;

    const groupKeys = Array.from(groupedData.keys());

    if (groupKeys.length === 0) return;

    setExpandedGroups((prev) => {
      const defaultExpandedGroups = groupingConfig?.defaultExpandedGroups;
      return groupKeys.reduce<Record<string, boolean>>((acc, key, index) => {
        // If the key already exists in prev, preserve its value
        if (prev[key] !== undefined) {
          acc[key] = prev[key];
        } else if (defaultExpandedGroups !== undefined) {
          // If defaultExpandedGroups is provided (even empty array), use it to determine if this group should be expanded
          acc[key] = defaultExpandedGroups.includes(key);
        } else {
          // If no defaultExpandedGroups, expand all groups by default
          acc[key] = true;
        }
        return acc;
      }, {});
    });
  }, [
    groupedData,
    groupingConfig?.isEnabled,
    groupingConfig?.defaultExpandedGroups,
    groupKeyFn,
  ]);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  return { groupedData, expandedGroups, toggleGroup, groupKeyFn } as const;
}
